// WICHTIG: Tabelle 'ai_feedback' muss in Supabase erstellt werden!
// SQL: siehe src/config/supabase-feedback-schema.sql

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getAiFeedbackHistory, AiFeedback } from '../lib/supabase';
import { getCourseList } from '../config/courses';

type FilterType = 'alle' | 'lernstand' | 'written_quiz';

export function FeedbackHistory() {
  const { courseId: urlCourseId } = useParams<{ courseId: string }>();
  const { user } = useAuth();
  const [feedbackList, setFeedbackList] = useState<AiFeedback[]>([]);
  const [typeFilter, setTypeFilter] = useState<FilterType>('alle');
  const [courseFilter, setCourseFilter] = useState<string>(urlCourseId || 'alle');
  const [isLoading, setIsLoading] = useState(true);

  const courses = getCourseList();

  useEffect(() => {
    if (user?.id) {
      loadFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, courseFilter]);

  const loadFeedback = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    // Pass courseId to filter server-side if not 'alle'
    const data = await getAiFeedbackHistory(
      user.id,
      50,
      courseFilter === 'alle' ? undefined : courseFilter
    );
    setFeedbackList(data);
    setIsLoading(false);
  };

  // Filter by type (client-side since we already have the data)
  const filtered = typeFilter === 'alle'
    ? feedbackList
    : feedbackList.filter(f => f.type === typeFilter);

  // Get course name for display
  const getCourseIcon = (id: string) => {
    const course = courses.find(c => c.id === id);
    return course?.icon || '';
  };

  const getCourseName = (id: string) => {
    const course = courses.find(c => c.id === id);
    return course?.title || id;
  };

  // Back link depends on whether we came from a specific course
  const backLink = urlCourseId ? `/lernstand/${urlCourseId}` : '/';

  if (isLoading) {
    return (
      <div style={{ padding: '20px', paddingBottom: '100px' }}>
        <Link to={backLink} className="back-link">
          <ArrowLeft size={18} />
          Zurueck
        </Link>
        <h1 style={{ color: '#ffd54f', fontSize: '22px', marginBottom: '16px', marginTop: '20px' }}>
          KI-Feedback Historie
        </h1>
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', marginTop: '40px' }}>
          Laden...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', paddingBottom: '100px' }}>
      <Link to={backLink} className="back-link">
        <ArrowLeft size={18} />
        Zurueck
      </Link>

      <h1 style={{ color: '#ffd54f', fontSize: '22px', marginBottom: '16px', marginTop: '20px' }}>
        KI-Feedback Historie
      </h1>

      {/* Course Filter */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '6px' }}>
          Kurs:
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setCourseFilter('alle')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: 'none',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              background: courseFilter === 'alle' ? '#ffd54f' : 'rgba(255,255,255,0.1)',
              color: courseFilter === 'alle' ? '#1a1a2e' : 'rgba(255,255,255,0.6)',
            }}
          >
            Alle Kurse
          </button>
          {courses.map(course => (
            <button
              key={course.id}
              onClick={() => setCourseFilter(course.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: 'none',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                background: courseFilter === course.id ? '#ffd54f' : 'rgba(255,255,255,0.1)',
                color: courseFilter === course.id ? '#1a1a2e' : 'rgba(255,255,255,0.6)',
              }}
            >
              {course.icon} {course.title}
            </button>
          ))}
        </div>
      </div>

      {/* Type Filter Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[
          { key: 'alle' as FilterType, label: 'Alle' },
          { key: 'lernstand' as FilterType, label: 'Lernstand' },
          { key: 'written_quiz' as FilterType, label: 'Schriftlich' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setTypeFilter(tab.key)}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: 'none',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              background: typeFilter === tab.key ? '#ab47bc' : 'rgba(255,255,255,0.1)',
              color: typeFilter === tab.key ? '#fff' : 'rgba(255,255,255,0.6)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Feedback Liste */}
      {filtered.map(item => (
        <div key={item.id} style={{
          background: item.type === 'lernstand'
            ? 'rgba(255, 213, 79, 0.08)'
            : 'rgba(171, 71, 188, 0.08)',
          border: `1px solid ${item.type === 'lernstand'
            ? 'rgba(255, 213, 79, 0.2)'
            : 'rgba(171, 71, 188, 0.2)'}`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px',
        }}>
          {/* Header: Typ + Kurs + Datum */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '12px',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px',
          }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{
                background: item.type === 'lernstand'
                  ? 'rgba(255, 213, 79, 0.2)'
                  : 'rgba(171, 71, 188, 0.2)',
                color: item.type === 'lernstand' ? '#ffd54f' : '#ce93d8',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 700,
              }}>
                {item.type === 'lernstand' ? 'Lernstand' : 'Quiz-Feedback'}
              </span>
              {/* Course badge */}
              {courseFilter === 'alle' && (
                <span style={{
                  background: 'rgba(66, 165, 245, 0.2)',
                  color: '#42a5f5',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  {getCourseIcon(item.course_id)} {getCourseName(item.course_id)}
                </span>
              )}
            </div>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
              {new Date(item.created_at).toLocaleDateString('de-DE', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </span>
          </div>

          {/* Score Info */}
          {item.score_info && (
            <div style={{
              color: '#4caf50',
              fontSize: '14px',
              fontWeight: 700,
              marginBottom: '8px'
            }}>
              {item.score_info}
            </div>
          )}

          {/* Bei written_quiz: Frage + Antwort zeigen */}
          {item.type === 'written_quiz' && (
            <>
              <div style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '13px',
                marginBottom: '4px'
              }}>
                Frage:
              </div>
              <div style={{
                color: '#e8eaf6',
                fontSize: '14px',
                marginBottom: '8px',
                fontWeight: 600,
              }}>
                {item.question}
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '13px',
                marginBottom: '4px'
              }}>
                Deine Antwort:
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                marginBottom: '12px',
                fontStyle: 'italic',
              }}>
                {item.user_answer}
              </div>
            </>
          )}

          {/* Parsed Feedback Sections */}
          {item.parsed_data && (
            <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
              {item.type === 'lernstand' ? (
                <>
                  {item.parsed_data.BEGRUESSUNG && (
                    <div style={{ color: '#e8eaf6', marginBottom: '8px' }}>
                      {item.parsed_data.BEGRUESSUNG}
                    </div>
                  )}
                  {item.parsed_data.EMPFEHLUNG && (
                    <div style={{ color: '#ffd54f', fontWeight: 600 }}>
                      {item.parsed_data.EMPFEHLUNG}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {item.parsed_data.PUNKTE && (
                    <div style={{ color: '#ffd54f', marginBottom: '4px' }}>
                      {item.parsed_data.PUNKTE}
                    </div>
                  )}
                  {item.parsed_data.RICHTIG && (
                    <div style={{ color: '#4caf50', marginBottom: '4px' }}>
                      {item.parsed_data.RICHTIG}
                    </div>
                  )}
                  {item.parsed_data.FEHLT && (
                    <div style={{ color: '#ff9800', marginBottom: '4px' }}>
                      {item.parsed_data.FEHLT}
                    </div>
                  )}
                  {item.parsed_data.GESAMT && (
                    <div style={{ color: '#ab47bc' }}>
                      {item.parsed_data.GESAMT}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Aufklappen fuer volle Antwort */}
          <details style={{ marginTop: '8px' }}>
            <summary style={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: '12px',
              cursor: 'pointer'
            }}>
              Komplettes KI-Feedback anzeigen
            </summary>
            <div style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '13px',
              lineHeight: '1.5',
              marginTop: '8px',
              whiteSpace: 'pre-wrap',
              background: 'rgba(0,0,0,0.2)',
              padding: '12px',
              borderRadius: '8px',
            }}>
              {item.ai_response}
            </div>
          </details>
        </div>
      ))}

      {filtered.length === 0 && !isLoading && (
        <div style={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.4)',
          marginTop: '40px'
        }}>
          Noch keine KI-Feedbacks vorhanden.
          <br />Mach ein Quiz um dein erstes Feedback zu bekommen!
        </div>
      )}
    </div>
  );
}
